import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { ProductConfigurationResponse } from '../../../api/ConfigApi';
import { PolicyData, withPolicyContext } from '../../../context/policy';
import {
	PRICE_PREVIEW_CHANGES_TABLE_COMMISSIONS_TITLE,
	PRICE_PREVIEW_CHANGES_TABLE_FEES_TITLE,
	PRICE_PREVIEW_CHANGES_TABLE_SUMMARY_TITLE,
	PRICE_PREVIEW_CHANGES_TABLE_TAXES_TITLE,
	PRICE_PREVIEW_FEE_DISPLAY_LABEL,
	PRICE_PREVIEW_GROSS_PREMIUM_LABEL,
	PRICE_PREVIEW_PREMIUM_LABEL,
	PRICE_PREVIEW_TAX_DISPLAY_LABEL,
	PRICE_PREVIEW_TECHNICAL_PREMIUM_LABEL,
	PRICE_PREVIEW_TOTAL_COMMISSIONS_LABEL,
	PRICE_PREVIEW_TOTAL_DUE_LABEL,
	PRICE_PREVIEW_TOTAL_FEES_LABEL,
	PRICE_PREVIEW_TOTAL_TAXES_LABEL,
} from '../../../data/labelsAndMessages';
import {
	AutomatedUnderwritingResult,
	PolicyPriceResponse,
} from '../../../types/policy';
import {
	formatCurrency,
	getEntityNameFrom_DescriptionField,
	getLastActiveExposureCharacteristic,
	getLastActivePerilCharacteristic,
	sortObjectsByString,
} from '../../../util/util';
import { PaddedDiv, StyledTitleBolded } from '../../common';
import UnderWritingDecisionsTable from '../UnderwritingDecisions';

interface Props {
	priceData: PolicyPriceResponse;
	productConfig: ProductConfigurationResponse;
	underwritingData: AutomatedUnderwritingResult;
	policyData: PolicyData;
}

function PricePreviewTables(props: Props) {
	const { t } = useTranslation();
	const { priceData, underwritingData, productConfig, policyData } = props;
	const { selectedQuote, policy } = policyData;
	const { policyConfiguration } = productConfig;
	const {
		grossPremium,
		fees,
		grossFees,
		taxGroups,
		grossTaxes,
		exposurePrices,
	} = priceData;
	const basePolicy = policy.issuedTimestamp ? policy : selectedQuote;
	const { exposures } = basePolicy;
	const currency = policy?.currency;

	const exposurePreviews = exposures.map((exposure, index) => {
		const { locator: exposureLocator, name: exposureName, perils } = exposure;
		const exposurePrice = exposurePrices.find(
			(expPrice: any) => expPrice.locator === exposureLocator,
		);

		if (!exposurePrice) {
			return null;
		}
		const exposureConfig = policyConfiguration.exposures.find(
			(exp) => exp.name === exposureName,
		);
		const exposureCharacteristic =
			getLastActiveExposureCharacteristic(exposure);
		const exposureDisplayName =
			getEntityNameFrom_DescriptionField(exposureCharacteristic) ||
			exposureConfig.displayName;
		const exposureTitle = `${t('exposure.singular.upper')} ${
			index + 1
		}: ${exposureDisplayName}`;
		const { grossPremium: exposureGrossPremium } = exposurePrice;
		const sortedPerils = sortObjectsByString(perils, 'name');

		const perilRows = sortedPerils.map((peril) => {
			const perilPrice = exposurePrice.perilPrices.find(
				(perPrice: any) => perPrice.locator === peril.locator,
			);
			const { premium, technicalPremium } = perilPrice;
			const technicalPremiumToDisplay = technicalPremium
				? formatCurrency(parseFloat(technicalPremium), currency)
				: 'N / A';
			const perilConfig = exposureConfig.perils.find(
				(p) => p.name === peril.name,
			);
			const perilCharacteristic = getLastActivePerilCharacteristic(peril);
			const perilDisplayName =
				getEntityNameFrom_DescriptionField(perilCharacteristic) ||
				perilConfig.displayName;

			return (
				<TableRow key={peril.locator}>
					<TableCell>{perilDisplayName}</TableCell>
					<TableCell align='right'>{technicalPremiumToDisplay}</TableCell>
					<TableCell align='right'>
						{formatCurrency(parseFloat(premium), currency)}
					</TableCell>
				</TableRow>
			);
		});

		return (
			<PaddedDiv data-cy={`exposure-${index}`} key={exposure.locator}>
				<StyledTitleBolded>{exposureTitle}</StyledTitleBolded>
				<TableContainer>
					<Table size='small'>
						<TableHead>
							<TableRow>
								<TableCell />
								<TableCell align='right'>
									{PRICE_PREVIEW_TECHNICAL_PREMIUM_LABEL()}
								</TableCell>
								<TableCell align='right'>
									{PRICE_PREVIEW_PREMIUM_LABEL()}
								</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{perilRows}
							<TableRow>
								<TableCell sx={{ fontWeight: 'bold' }}>
									{PRICE_PREVIEW_GROSS_PREMIUM_LABEL()}
								</TableCell>
								<TableCell />
								<TableCell align='right' sx={{ fontWeight: 'bold' }}>
									{formatCurrency(parseFloat(exposureGrossPremium), currency)}
								</TableCell>
							</TableRow>
						</TableBody>
					</Table>
				</TableContainer>
			</PaddedDiv>
		);
	});

	const renderTaxes = () => {
		if (taxGroups && taxGroups.length > 0) {
			const taxRows = taxGroups.map((tax, index) => {
				const { name: taxName, amount } = tax;
				const taxConfig = policyConfiguration.taxes.find(
					(t) => t.name === taxName,
				);
				const taxTitle = taxConfig
					? `${PRICE_PREVIEW_TAX_DISPLAY_LABEL(index + 1)}: ${
							taxConfig.displayName
					  }`
					: '';

				return (
					<TableRow key={`tax-${index}`}>
						<TableCell>{taxTitle}</TableCell>
						<TableCell align='right'>
							{formatCurrency(parseFloat(amount), currency)}
						</TableCell>
					</TableRow>
				);
			});

			return (
				<PaddedDiv data-cy='taxes-table'>
					<StyledTitleBolded>
						{PRICE_PREVIEW_CHANGES_TABLE_TAXES_TITLE()}
					</StyledTitleBolded>
					<TableContainer>
						<Table size='small'>
							<TableBody>
								{taxRows}
								<TableRow key={`total-tax`}>
									<TableCell sx={{ fontWeight: 'bold' }}>
										{PRICE_PREVIEW_TOTAL_TAXES_LABEL()}
									</TableCell>
									<TableCell align='right' sx={{ fontWeight: 'bold' }}>
										{formatCurrency(parseFloat(grossTaxes), currency)}
									</TableCell>
								</TableRow>
							</TableBody>
						</Table>
					</TableContainer>
				</PaddedDiv>
			);
		} else {
			return null;
		}
	};

	const renderFee = () => {
		if (fees && fees.length > 0) {
			const feeRows = fees.map((fee, index) => {
				const { name: feeName, amount } = fee;
				const feeConfig = policyConfiguration.fees.find(
					(f) => f.name === feeName,
				);
				const feeTitle = feeConfig
					? `${PRICE_PREVIEW_FEE_DISPLAY_LABEL(index + 1)}: ${
							feeConfig.displayName
					  }`
					: '';

				return (
					<TableRow key={`fee-${index}`}>
						<TableCell>{feeTitle}</TableCell>
						<TableCell align='right'>
							{formatCurrency(parseFloat(amount), currency)}
						</TableCell>
					</TableRow>
				);
			});

			return (
				<PaddedDiv data-cy='fees-table'>
					<StyledTitleBolded>
						{PRICE_PREVIEW_CHANGES_TABLE_FEES_TITLE()}
					</StyledTitleBolded>
					<TableContainer>
						<Table size='small'>
							<TableBody>
								{feeRows}
								<TableRow key={`total-fee`}>
									<TableCell sx={{ fontWeight: 'bold' }}>
										{PRICE_PREVIEW_TOTAL_FEES_LABEL()}
									</TableCell>
									<TableCell align='right' sx={{ fontWeight: 'bold' }}>
										{formatCurrency(parseFloat(grossFees), currency)}
									</TableCell>
								</TableRow>
							</TableBody>
						</Table>
					</TableContainer>
				</PaddedDiv>
			);
		} else {
			return null;
		}
	};

	const renderCommissions = () => {
		const { commissions, grossCommissions } = priceData;
		if (commissions && commissions.length > 0) {
			const commissionRows = commissions.map((commission, index) => {
				const { recipient, amount } = commission;
				const commissionTitle = `Recipient ${index + 1}: ${recipient}`;
				return (
					<TableRow key={`commission-${index}`}>
						<TableCell>{commissionTitle}</TableCell>
						<TableCell align='right'>
							{formatCurrency(parseFloat(amount), currency)}
						</TableCell>
					</TableRow>
				);
			});

			return (
				<PaddedDiv data-cy={`${basePolicy.locator}-commission`}>
					<StyledTitleBolded>
						{PRICE_PREVIEW_CHANGES_TABLE_COMMISSIONS_TITLE()}
					</StyledTitleBolded>
					<TableContainer>
						<Table size='small'>
							<TableBody>
								{commissionRows}
								<TableRow key={`total-commissions`}>
									<TableCell sx={{ fontWeight: 'bold' }}>
										{PRICE_PREVIEW_TOTAL_COMMISSIONS_LABEL()}
									</TableCell>
									<TableCell align='right' sx={{ fontWeight: 'bold' }}>
										{formatCurrency(parseFloat(grossCommissions), currency)}
									</TableCell>
								</TableRow>
							</TableBody>
						</Table>
					</TableContainer>
				</PaddedDiv>
			);
		} else {
			return null;
		}
	};

	const renderAutomatedUnderwritingResult = () => {
		return underwritingData ? (
			<UnderWritingDecisionsTable
				underwritingData={underwritingData}
				basePolicyLocator={basePolicy.locator}
			/>
		) : null;
	};

	const renderSummaryTable = () => {
		const totalDue =
			parseFloat(grossPremium) + parseFloat(grossFees) + parseFloat(grossTaxes);
		return (
			<>
				<StyledTitleBolded>
					{PRICE_PREVIEW_CHANGES_TABLE_SUMMARY_TITLE()}
				</StyledTitleBolded>
				<TableContainer>
					<Table size='small'>
						<TableBody>
							<TableRow>
								<TableCell>{PRICE_PREVIEW_GROSS_PREMIUM_LABEL()}</TableCell>
								<TableCell align='right'>
									{formatCurrency(parseFloat(grossPremium), currency)}
								</TableCell>
							</TableRow>
							<TableRow>
								<TableCell>
									{PRICE_PREVIEW_CHANGES_TABLE_FEES_TITLE()}
								</TableCell>
								<TableCell align='right'>
									{formatCurrency(parseFloat(grossFees), currency)}
								</TableCell>
							</TableRow>
							<TableRow>
								<TableCell>
									{PRICE_PREVIEW_CHANGES_TABLE_TAXES_TITLE()}
								</TableCell>
								<TableCell align='right'>
									{formatCurrency(parseFloat(grossTaxes), currency)}
								</TableCell>
							</TableRow>
							<TableRow>
								<TableCell sx={{ fontWeight: 'bold' }}>
									{PRICE_PREVIEW_TOTAL_DUE_LABEL()}
								</TableCell>
								<TableCell align='right' sx={{ fontWeight: 'bold' }}>
									{formatCurrency(totalDue, currency)}
								</TableCell>
							</TableRow>
						</TableBody>
					</Table>
				</TableContainer>
			</>
		);
	};

	return (
		<>
			{renderSummaryTable()}
			{exposurePreviews}
			{renderFee()}
			{renderTaxes()}
			{renderCommissions()}
			{renderAutomatedUnderwritingResult()}
		</>
	);
}

export default withPolicyContext<Props>(PricePreviewTables);
